<template>
  <!-- Request Details Modal -->
  <div v-if="show" class="modal show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title">
            <i class="bx bx-file-blank me-2"></i>
            Request Details - {{request.referenceNumber}}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading request details...</p>
          </div>

          <div v-else>
            <!-- Request Overview -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="card border-primary">
                  <div class="card-header bg-primary">
                    <h6 class="mb-0 text-white">
                      <i class="bx bx-info-circle me-2"></i>
                      Request Information
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-6">
                        <p class="mb-2"><strong>Reference:</strong></p>
                        <p class="text-primary font-weight-bold">{{request.referenceNumber}}</p>
                      </div>
                      <div class="col-6">
                        <p class="mb-2"><strong>Status:</strong></p>
                        <span class="badge" :class="getStatusBadge(request.status)">
                          {{request.status?.toUpperCase()}}
                        </span>
                      </div>
                    </div>
                    <div class="row mt-3">
                      <div class="col-6">
                        <p class="mb-2"><strong>Submitted:</strong></p>
                        <p class="mb-0">{{formatDate(request.submitted_date)}}</p>
                      </div>
                      <div class="col-6">
                        <p class="mb-2"><strong>Processed:</strong></p>
                        <p class="mb-0">{{request.processed_date ? formatDate(request.processed_date) : 'Not processed'}}</p>
                      </div>
                    </div>
                    <div class="row mt-3" v-if="request.processed_by">
                      <div class="col-12">
                        <p class="mb-2"><strong>Processed By:</strong></p>
                        <p class="mb-0">{{request.processed_by}} ({{request.processed_by_id_no}})</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card border-info">
                  <div class="card-header bg-info text-white">
                    <h6 class="mb-0 text-white">
                      <i class="bx bx-home me-2 text-white"></i>
                      Property Information
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-12">
                        <p class="mb-2"><strong>LR Number:</strong></p>
                        <p class="text-info font-weight-bold">{{request.LRNumber}}</p>
                      </div>
                    </div>
                    <div class="row mt-3">
                      <div class="col-12">
                        <p class="mb-2"><strong>Current Owner:</strong></p>
                        <p class="mb-0">{{request.ownerNames}}</p>
                      </div>
                    </div>
                    <div class="row mt-3">
                      <div class="col-12">
                        <p class="mb-2"><strong>Update Type:</strong></p>
                        <span class="badge bg-soft-info text-info">{{request.update_type}}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Current vs Requested Data Comparison -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">
                      <i class="bx bx-git-compare me-2"></i>
                      Current vs Requested Changes
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row" v-if="currentData && requestedData">
                      <div class="col-md-6">
                        <h6 class="text-secondary mb-3">Current Data</h6>
                        <div class="current-data bg-light p-3 rounded">
                          <div v-if="hasOwnerInfoChanges" class="mb-3">
                            <h6 class="text-primary">Owner Information</h6>
                            <p v-if="currentData.ownerNames" class="mb-1"><strong>Name:</strong> {{currentData.ownerNames}}</p>
                            <p v-if="currentData.ownerPhoneNo" class="mb-1"><strong>Phone:</strong> {{currentData.ownerPhoneNo}}</p>
                            <p v-if="currentData.ownerEmail" class="mb-1"><strong>Email:</strong> {{currentData.ownerEmail}}</p>
                            <p v-if="currentData.ownerID" class="mb-1"><strong>ID:</strong> {{currentData.ownerID}}</p>
                            <p v-if="currentData.physicalAddress" class="mb-0"><strong>Address:</strong> {{currentData.physicalAddress}}</p>
                          </div>

                          <div v-if="hasPropertyDetailsChanges" class="mb-3">
                            <h6 class="text-primary">Property Details</h6>
                            <p v-if="currentData.percelAddress" class="mb-1"><strong>Location:</strong> {{currentData.percelAddress}}</p>
                            <p v-if="currentData.useName" class="mb-0"><strong>Use:</strong> {{currentData.useName}}</p>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6">
                        <h6 class="text-success mb-3">Requested Changes</h6>
                        <div class="requested-data bg-success-light p-3 rounded">
                          <div v-if="hasOwnerInfoChanges" class="mb-3">
                            <h6 class="text-success">Owner Information</h6>
                            <p v-if="requestedData.ownerNames" class="mb-1">
                              <strong>Name:</strong>
                              <span :class="{'text-success font-weight-bold': requestedData.ownerNames !== currentData.ownerNames}">
                                {{requestedData.ownerNames}}
                              </span>
                            </p>
                            <p v-if="requestedData.ownerPhoneNo" class="mb-1">
                              <strong>Phone:</strong>
                              <span :class="{'text-success font-weight-bold': requestedData.ownerPhoneNo !== currentData.ownerPhoneNo}">
                                {{requestedData.ownerPhoneNo}}
                              </span>
                            </p>
                            <p v-if="requestedData.ownerEmail" class="mb-1">
                              <strong>Email:</strong>
                              <span :class="{'text-success font-weight-bold': requestedData.ownerEmail !== currentData.ownerEmail}">
                                {{requestedData.ownerEmail}}
                              </span>
                            </p>
                            <p v-if="requestedData.ownerID" class="mb-1">
                              <strong>ID:</strong>
                              <span :class="{'text-success font-weight-bold': requestedData.ownerID !== currentData.ownerID}">
                                {{requestedData.ownerID}}
                              </span>
                            </p>
                            <p v-if="requestedData.physicalAddress" class="mb-0">
                              <strong>Address:</strong>
                              <span :class="{'text-success font-weight-bold': requestedData.physicalAddress !== currentData.physicalAddress}">
                                {{requestedData.physicalAddress}}
                              </span>
                            </p>
                          </div>

                          <div v-if="hasPropertyDetailsChanges" class="mb-3">
                            <h6 class="text-success">Property Details</h6>
                            <p v-if="requestedData.percelAddress" class="mb-1">
                              <strong>Location:</strong>
                              <span :class="{'text-success font-weight-bold': requestedData.percelAddress !== currentData.percelAddress}">
                                {{requestedData.percelAddress}}
                              </span>
                            </p>
                            <p v-if="requestedData.useName" class="mb-0">
                              <strong>Use:</strong>
                              <span :class="{'text-success font-weight-bold': requestedData.useName !== currentData.useName}">
                                {{requestedData.useName}}
                              </span>
                            </p>
                          </div>

                          <div v-if="hasOwnershipTransfer" class="mb-3">
                            <h6 class="text-warning">Ownership Transfer</h6>
                            <p v-if="requestedData.newOwnerName" class="mb-1"><strong>New Owner:</strong> {{requestedData.newOwnerName}}</p>
                            <p v-if="requestedData.newOwnerID" class="mb-1"><strong>New Owner ID:</strong> {{requestedData.newOwnerID}}</p>
                            <p v-if="requestedData.transferType" class="mb-1"><strong>Transfer Type:</strong> {{requestedData.transferType}}</p>
                            <p v-if="requestedData.transferDate" class="mb-0"><strong>Transfer Date:</strong> {{formatDate(requestedData.transferDate)}}</p>
                          </div>

                          <div v-if="hasImprovements" class="mb-3">
                            <h6 class="text-primary">Property Improvements</h6>
                            <p v-if="requestedData.improvements" class="mb-1"><strong>Description:</strong> {{requestedData.improvements}}</p>
                            <p v-if="requestedData.improvementCost" class="mb-1"><strong>Cost:</strong> {{formatPrice(requestedData.improvementCost)}}</p>
                            <p v-if="requestedData.improvementDate" class="mb-0"><strong>Completion Date:</strong> {{formatDate(requestedData.improvementDate)}}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Requester Information -->
            <div class="row mt-4" v-if="requesterInfo">
              <div class="col-12">
                <div class="card border-warning">
                  <div class="card-header bg-warning text-dark">
                    <h6 class="mb-0">
                      <i class="bx bx-user me-2"></i>
                      Requester Information
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-3">
                        <p class="mb-2"><strong>Name:</strong></p>
                        <p class="mb-0">{{requesterInfo.fullName}}</p>
                      </div>
                      <div class="col-md-3">
                        <p class="mb-2"><strong>ID Number:</strong></p>
                        <p class="mb-0">{{requesterInfo.idNumber}}</p>
                      </div>
                      <div class="col-md-3">
                        <p class="mb-2"><strong>Phone:</strong></p>
                        <p class="mb-0">{{requesterInfo.phoneNumber}}</p>
                      </div>
                      <div class="col-md-3">
                        <p class="mb-2"><strong>Relationship:</strong></p>
                        <p class="mb-0">{{requesterInfo.relationship}}</p>
                      </div>
                    </div>
                    <div class="row mt-3" v-if="requesterInfo.email">
                      <div class="col-md-6">
                        <p class="mb-2"><strong>Email:</strong></p>
                        <p class="mb-0">{{requesterInfo.email}}</p>
                      </div>
                    </div>
                    <div class="row mt-3" v-if="requesterInfo.authorizationDetails">
                      <div class="col-12">
                        <p class="mb-2"><strong>Authorization Details:</strong></p>
                        <p class="mb-0">{{requesterInfo.authorizationDetails}}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Notes Section -->
            <div class="row mt-4" v-if="request.notes">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">
                      <i class="bx bx-note me-2"></i>
                      Administrative Notes
                    </h6>
                  </div>
                  <div class="card-body">
                    <p class="mb-0">{{request.notes}}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'RequestDetailsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    request: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      currentData: null,
      requestedData: null,
      requesterInfo: null
    }
  },
  computed: {
    hasOwnerInfoChanges() {
      return this.request.update_type?.includes('Owner Information');
    },
    hasPropertyDetailsChanges() {
      return this.request.update_type?.includes('Property Details');
    },
    hasOwnershipTransfer() {
      return this.request.update_type?.includes('Ownership Transfer');
    },
    hasImprovements() {
      return this.request.update_type?.includes('Property Improvements');
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.request.id) {
        this.parseRequestData();
      }
    }
  },
  methods: {
    parseRequestData() {
      try {
        this.currentData = this.request.current_data ? JSON.parse(this.request.current_data) : null;
        this.requestedData = this.request.requested_data ? JSON.parse(this.request.requested_data) : null;
        this.requesterInfo = this.request.requester_info ? JSON.parse(this.request.requester_info) : null;
      } catch (error) {
        console.error('Error parsing request data:', error);
      }
    },

    getStatusBadge(status) {
      const badges = {
        'pending': 'bg-warning text-dark',
        'under_review': 'bg-info text-white',
        'approved': 'bg-success text-white',
        'rejected': 'bg-danger text-white',
        'requires_documents': 'bg-secondary text-white'
      };
      return badges[status] || 'bg-secondary text-white';
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('DD/MM/YYYY HH:mm');
    },

    formatPrice(value) {
      const formatter = new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      return formatter.format(value || 0);
    },

    closeModal() {
      this.$emit('close');
    },

    approveRequest() {
      this.$emit('approve', this.request);
      this.closeModal();
    },

    rejectRequest() {
      this.$emit('reject', this.request);
      this.closeModal();
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.bg-success-light {
  background-color: #f8fff9;
  border: 1px solid #d4edda;
}

.text-success.font-weight-bold {
  font-weight: 600 !important;
}

.card {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border-radius: 8px;
}

.card-header {
  border-radius: 8px 8px 0 0;
}

.requested-data p,
.current-data p {
  font-size: 14px;
  line-height: 1.4;
}

.badge {
  font-size: 12px;
  padding: 6px 12px;
}

@media (max-width: 768px) {
  .modal-dialog {
    max-width: 95%;
    margin: 10px auto;
  }

  .modal-body {
    padding: 15px;
  }
}
</style>